import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useCompassContext } from "@circleco/compass/context/CompassContext";
import { Popover as HeadlessPopover } from "@headlessui/react";
import { useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { ConnectPopoverContext } from "@/react/components/shared/Connect/ConnectPopoverContext";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";

import { DEFAULT_POPPER_OPTIONS } from "../constants";
import { BodyUIKit } from "./BodyUIKit";
import { ConnectPopoverButton } from "./ConnectPopoverButton";
import { HeaderUIKit } from "./HeaderUIKit";

const isPopoverPanel = true;

export const Connect = () => {
  const compassContext = useCompassContext();
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const isConnectEnabled = useIsConnectEnabled();
  const location = useLocation();
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "right-end",
    ...DEFAULT_POPPER_OPTIONS,
  });

  useEffect(() => {
    if (
      location.hash === "connections" ||
      window.location.hash === "#connections"
    ) {
      referenceElement?.click();
    }
  }, [location.hash, referenceElement]);

  if (!isConnectEnabled) return null;

  const portalRoot = compassContext?.portalContainer ?? document.body;

  return (
    <HeadlessPopover>
      {({ open, close }) => (
        <>
          <HeadlessPopover.Button
            as="div"
            ref={setReferenceElement}
            className={classNames("w-full", {
              "rounded-md": open,
            })}
          >
            <ConnectPopoverButton isOpen={open} />
          </HeadlessPopover.Button>
          {createPortal(
            <HeadlessPopover.Panel
              as="div"
              ref={setPopperElement}
              style={{ ...styles.popper, pointerEvents: "auto" }}
              {...attributes.popper}
              className={classNames(
                "border-primary bg-primary z-50 -ml-2.5 flex max-h-[45rem] min-h-[37rem] max-w-[30rem] flex-col overflow-hidden rounded-md border shadow-lg",
                {
                  "cursor-not-allowed": isViewOnlyMasquerading,
                },
              )}
              data-testid="connect-panel-container"
            >
              <ConnectPopoverContext.Provider value={isPopoverPanel}>
                <div
                  className={classNames(
                    "flex min-h-0 flex-1 flex-col rounded-md",
                    {
                      "pointer-events-none": isViewOnlyMasquerading,
                    },
                  )}
                >
                  <HeaderUIKit onClose={close} />
                  <BodyUIKit onClose={close} />
                </div>
              </ConnectPopoverContext.Provider>
            </HeadlessPopover.Panel>,
            portalRoot,
          )}
        </>
      )}
    </HeadlessPopover>
  );
};
